import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  LocationSearchField,
  ServerErrorCallout,
  Switch,
  Text,
} from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useCurrentUser } from '@/contexts';
import { locationService, notificationService, settingsService, userService } from '@/services';
import { formatLocationOverride, getCityCoordinates, type PlaceSearchResult } from '@/utils/geo';

export function PreferencesPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { currentUser: profile, settings, isAdmin } = useCurrentUser();
  const uid = profile?.uid;
  const [saving, setSaving] = useState<'location' | 'notifications' | 'override' | null>(null);

  const [notifPerm, setNotifPerm] = useState<NotificationPermission>(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [geoPerm, setGeoPerm] = useState<PermissionState | 'unknown'>('unknown');

  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(status => {
          setGeoPerm(status.state as PermissionState);
          status.onchange = () => setGeoPerm(status.state as PermissionState);
        })
        .catch(() => setGeoPerm('unknown'));
    }
  }, []);

  const notificationsEnabled = settings?.notificationsEnabled ?? false;
  const locationAllowed = settings?.locationAllowed ?? false;
  const locationOverride = isAdmin ? settings?.locationOverride : null;
  const overrideLabel = formatLocationOverride(locationOverride);

  const blockedMessage = useMemo(() => {
    const parts: string[] = [];
    if (notifPerm === 'denied') parts.push(t('preferences.notificationsBlocked'));
    if (geoPerm === 'denied') parts.push(t('preferences.locationBlocked'));
    return parts.join(' ');
  }, [notifPerm, geoPerm, t]);

  const handleNotificationToggle = async (checked: boolean) => {
    if (!uid || saving || notifPerm === 'denied') return;
    setSaving('notifications');

    try {
      if (checked) {
        const permission = await notificationService.requestPermission();
        setNotifPerm(permission ?? 'default');
        if (permission !== 'granted') {
          return;
        }
        await notificationService.registerMessaging(uid);
      } else {
        await notificationService.unregisterMessaging(uid);
      }

      await settingsService.updateSettings(uid, { notificationsEnabled: checked });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not update notification preference';
      console.error('Failed to update notification preference:', err);
      toast.error(message);
    } finally {
      setSaving(null);
    }
  };

  const handleLocationToggle = async (checked: boolean) => {
    if (!uid || saving || geoPerm === 'denied') return;
    setSaving('location');

    try {
      if (checked) {
        if (!locationService.isSupported()) {
          toast.error(t('location.unsupported'));
          return;
        }

        const coords = await locationService.getCurrentLocation();
        if (!coords) {
          toast.error(t('location.denied'));
          return;
        }

        await Promise.all([
          userService.updateProfile(uid, { ...coords }),
          settingsService.updateSettings(uid, { locationAllowed: true }),
        ]);
        toast.success(t('location.activated'));
      } else {
        const cityCoords = profile?.city ? await getCityCoordinates(profile.city) : null;
        await Promise.all([
          settingsService.updateSettings(uid, { locationAllowed: false }),
          userService.updateProfile(
            uid,
            cityCoords ?? { latitude: undefined, longitude: undefined }
          ),
        ]);
      }
    } catch (err) {
      console.error('Failed to update location preference:', err);
      toast.error(t('location.error'));
    } finally {
      setSaving(null);
    }
  };

  const handleOverrideSelect = async (place: PlaceSearchResult) => {
    if (!uid || saving) return;
    setSaving('override');

    try {
      await settingsService.updateSettings(uid, {
        locationOverride: {
          latitude: place.latitude,
          longitude: place.longitude,
          label: place.label,
        },
      });
      toast.success(t('location.override.saved', { place: place.label }));
    } catch (err) {
      console.error('Failed to set location override:', err);
      toast.error(t('location.override.error'));
    } finally {
      setSaving(null);
    }
  };

  const handleOverrideClear = async () => {
    if (!uid || saving) return;
    setSaving('override');

    try {
      await settingsService.updateSettings(uid, { locationOverride: null });
    } catch (err) {
      console.error('Failed to clear location override:', err);
      toast.error(t('location.override.error'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <AccountSubpageShell
      onBack={() => navigate('/account/settings')}
      title={t('preferences.title')}
      subtitle={t('preferences.subtitle')}
    >
      <Flex direction="column" gap="4" pt="4">
        {blockedMessage ? <ServerErrorCallout message={blockedMessage} /> : null}

        <Flex direction="row" align="center" justify="between" gap="3">
          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="bold">
              {t('hub.notifications.title')}
            </Text>
            <Text as="div" size="1" color="gray">
              {t('hub.notifications.subtitle')}
            </Text>
          </Flex>
          <Switch
            checked={notificationsEnabled}
            disabled={saving === 'notifications' || !uid || notifPerm === 'denied'}
            onCheckedChange={checked => void handleNotificationToggle(checked)}
          />
        </Flex>

        <Flex direction="row" align="center" justify="between" gap="3">
          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="bold">
              {t('location.title')}
            </Text>
            <Text as="div" size="1" color="gray">
              {t('location.subtitle')}
            </Text>
          </Flex>
          <Switch
            checked={locationAllowed}
            disabled={saving === 'location' || !uid || geoPerm === 'denied'}
            onCheckedChange={checked => void handleLocationToggle(checked)}
          />
        </Flex>

        {isAdmin && (
          <Flex direction="column" gap="2">
            <Flex direction="column" gap="1">
              <Text size="2" weight="bold">
                {t('location.override.title')}
              </Text>
              <Text size="1" color="gray">
                {t('location.override.subtitle')}
              </Text>
              {locationOverride && (
                <Text size="1" color="orange">
                  {t('location.override.active', { place: overrideLabel })}
                </Text>
              )}
            </Flex>
            <Flex direction="row" gap="2" align="start">
              <Flex direction="column" gap="1" flexGrow="1">
                <LocationSearchField
                  key={overrideLabel || 'empty'}
                  defaultValue={overrideLabel}
                  placeholder={t('location.override.placeholder')}
                  disabled={!uid || saving === 'override'}
                  onPlaceSelect={place => void handleOverrideSelect(place)}
                />
              </Flex>
              {locationOverride && (
                <Button
                  type="button"
                  variant="outline"
                  color="gray"
                  disabled={saving === 'override'}
                  onClick={() => void handleOverrideClear()}
                >
                  {t('location.override.clear')}
                </Button>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
    </AccountSubpageShell>
  );
}
